/**
 * Eenvoudige procedurele audio voor "timer afgelopen" geluiden.
 * Werkt via de Web Audio API — geen externe bestanden nodig, dus geen CORS,
 * geen downloads, en altijd betrouwbaar.
 *
 * Beschikbare geluiden: 'bell' | 'gong' | 'alarm'
 * Iedere `play()` aanroep speelt het geluid een paar keer luid achter elkaar
 * zodat de gebruiker het altijd hoort.
 */

let _ctx = null;
function getCtx() {
  if (!_ctx) {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    _ctx = new AC();
  }
  if (_ctx.state === 'suspended') {
    _ctx.resume().catch(() => {});
  }
  return _ctx;
}

/**
 * Speel één bel-tik af (bell-achtige decay) op tijdstip `when` (seconden in audio time).
 * `freq` is de grondfrequentie van de bel.
 */
function playBellHit(ctx, when, freq = 880, volume = 0.9, duration = 1.6) {
  // Master gain met snel attack + langzame decay (bel-envelope)
  const master = ctx.createGain();
  master.gain.setValueAtTime(0, when);
  master.gain.linearRampToValueAtTime(volume, when + 0.005);
  master.gain.exponentialRampToValueAtTime(0.0001, when + duration);
  master.connect(ctx.destination);

  // Compositie: grondtoon + paar boventonen (klassieke bel klinkt zo)
  const partials = [
    { ratio: 1.0, gain: 1.0 },
    { ratio: 2.0, gain: 0.5 },
    { ratio: 3.0, gain: 0.3 },
    { ratio: 4.2, gain: 0.2 },
    { ratio: 5.4, gain: 0.15 },
  ];
  partials.forEach(p => {
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = freq * p.ratio;
    const g = ctx.createGain();
    g.gain.value = p.gain;
    osc.connect(g).connect(master);
    osc.start(when);
    osc.stop(when + duration);
  });
}

function playGongHit(ctx, when, volume = 0.9, duration = 2.5) {
  // Lager, voller geluid met meer inharmonische partialen
  const master = ctx.createGain();
  master.gain.setValueAtTime(0, when);
  master.gain.linearRampToValueAtTime(volume, when + 0.02);
  master.gain.exponentialRampToValueAtTime(0.0001, when + duration);
  master.connect(ctx.destination);

  const partials = [
    { freq: 110, gain: 1.0 },
    { freq: 220, gain: 0.7 },
    { freq: 311, gain: 0.5 },
    { freq: 415, gain: 0.4 },
    { freq: 587, gain: 0.25 },
    { freq: 740, gain: 0.18 },
  ];
  partials.forEach(p => {
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = p.freq;
    const g = ctx.createGain();
    g.gain.value = p.gain;
    osc.connect(g).connect(master);
    osc.start(when);
    osc.stop(when + duration);
  });
}

function playAlarmBeep(ctx, when, volume = 0.9, duration = 0.18) {
  // Korte luide pieptoon (alarm-stijl)
  const g = ctx.createGain();
  g.gain.setValueAtTime(0, when);
  g.gain.linearRampToValueAtTime(volume, when + 0.005);
  g.gain.setValueAtTime(volume, when + duration - 0.01);
  g.gain.linearRampToValueAtTime(0, when + duration);
  g.connect(ctx.destination);

  const osc = ctx.createOscillator();
  osc.type = 'square';
  osc.frequency.value = 1000;
  osc.connect(g);
  osc.start(when);
  osc.stop(when + duration);
}

/**
 * Speel het complete eindgeluid af. Returnt true bij succes, false bij geen audio support.
 * `key` bepaalt het geluid; geluid wordt meerdere keren herhaald voor extra luidheid.
 */
export function playEndSound(key = 'bell') {
  const ctx = getCtx();
  if (!ctx) return false;

  const start = ctx.currentTime + 0.02;

  if (key === 'gong') {
    // 3 gongs met overlap
    playGongHit(ctx, start);
    playGongHit(ctx, start + 1.0);
    playGongHit(ctx, start + 2.0);
  } else if (key === 'alarm') {
    // 8 snelle alarm beeps
    for (let i = 0; i < 8; i++) {
      playAlarmBeep(ctx, start + i * 0.22);
    }
  } else {
    // Default: bell, 5 keer met afnemende dichtheid
    playBellHit(ctx, start, 880, 0.95, 1.8);
    playBellHit(ctx, start + 0.45, 880, 0.85, 1.6);
    playBellHit(ctx, start + 0.9, 1100, 0.8, 1.4);
    playBellHit(ctx, start + 1.35, 880, 0.75, 1.4);
    playBellHit(ctx, start + 2.0, 660, 0.7, 1.8);
  }

  return true;
}

/**
 * Test/preview functie — speelt een korte preview van het gekozen geluid.
 */
export function previewSound(key = 'bell') {
  const ctx = getCtx();
  if (!ctx) return false;
  const start = ctx.currentTime + 0.02;
  if (key === 'gong') {
    playGongHit(ctx, start, 0.7, 1.8);
  } else if (key === 'alarm') {
    playAlarmBeep(ctx, start);
    playAlarmBeep(ctx, start + 0.22);
    playAlarmBeep(ctx, start + 0.44);
  } else {
    playBellHit(ctx, start, 880, 0.8, 1.4);
  }
  return true;
}

/**
 * Roep dit aan bij een user-gesture (klik, touch) om de AudioContext
 * "te ontgrendelen" — sommige browsers blokkeren audio tot een user-actie.
 */
export function unlockAudio() {
  const ctx = getCtx();
  if (!ctx) return false;
  // Een hele korte, zo goed als onhoorbare ping om de context "warm" te maken
  try {
    const g = ctx.createGain();
    g.gain.value = 0.0001;
    const osc = ctx.createOscillator();
    osc.frequency.value = 440;
    osc.connect(g).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.02);
    return true;
  } catch (e) {
    return false;
  }
}

// ─── LOOPING ALARM ──────────────────────────────────────────────────────
// Voor de Pomodoro Timer: bij einde focus blijft de alarm loopen tot user
// op "Start Pauze" klikt. We gebruiken een setInterval die periodiek het
// gekozen geluid afspeelt.
let _loopInterval = null;
let _loopActive = false;

/**
 * Start een loopend alarm dat blijft afspelen tot stopLoopingAlarm() wordt
 * aangeroepen. `key` is een van 'bell' | 'gong' | 'alarm'.
 * Het interval is afgestemd op de duur van het geluid zodat het naadloos
 * doorloopt zonder te overlappen.
 */
export function startLoopingAlarm(key = 'bell') {
  // Stop eventuele bestaande loop
  stopLoopingAlarm();
  const ctx = getCtx();
  if (!ctx) return false;

  _loopActive = true;
  // Speel meteen één keer af
  playEndSound(key);

  // Bepaal herhaal-interval op basis van geluid (in ms)
  // bell: ~3.5s totale klank, gong: ~3s, alarm: ~2s
  const intervalMs = key === 'alarm' ? 2200 : key === 'gong' ? 3200 : 3500;

  _loopInterval = setInterval(() => {
    if (!_loopActive) return;
    playEndSound(key);
  }, intervalMs);

  return true;
}

/**
 * Stop de loopende alarm. Veilig om meermaals aan te roepen.
 */
export function stopLoopingAlarm() {
  _loopActive = false;
  if (_loopInterval) {
    clearInterval(_loopInterval);
    _loopInterval = null;
  }
}

export function isAlarmLooping() {
  return _loopActive;
}
